'use strict';

if (ISDEBUG) { console.log("SignInOut.js: Top of script"); }

import * as S from 'sanctuary';
import * as $ from 'sanctuary-def';

import {node, encase, encaseP, chain, map, fork, Future} from 'fluture/index.js'

// New imports to utilize smNapOnlBackend
//import awsconfig from '../aws-exports';
//Modify the test/dev cognito user pool settings in awsconfig to be the production user pool before configure AUTH
// awsconfig.aws_user_pools_id = "ap-southeast-2_CnELLRYZ9"
// awsconfig.aws_user_pools_web_client_id = "2giaqi5dijgvdevi6gimbea734"
// awsconfig.aws_cognito_identity_pool_id = "ap-southeast-2:376b7ddd-f137-47f5-9aa7-3a98e34293a7"

// import API, { graphqlOperation } from '@aws-amplify/api';
import { Auth } from '@aws-amplify/auth';
//import { Auth } from 'aws-amplify';

// Import the new modern Cognito service
import { CognitoService, MFARequiredError, AuthenticationError } from '../services/CognitoService';

// Import awsconfig for Cognito configuration values
import awsconfig from '../aws-exports';

import { ERROR_TYPE, INTEROP_TAGS } from './Constants.js'

// Feature flag to toggle between old and new authentication systems
// Set to true to use the new AWS SDK v3 Cognito service
// Set to false to use the legacy AWS SDK v2 implementation
const USE_MODERN_COGNITO = true;

if (ISDEBUG) { 
    console.log('SignInOut.js: Authentication system:', USE_MODERN_COGNITO ? 'MODERN (AWS SDK v3)' : 'LEGACY (AWS SDK v2)'); 
}

// Global variables to store MFA state across function calls
let mfaCredentials = null;
let mfaSession = null;
let mfaUserPoolId = null;
let mfaClientId = null;

// NEW: Modern authentication using AWS SDK v3 Cognito service
const modernAuthSignIn = async (credentials) => {
    try {
        if (ISDEBUG) { console.log('Modern auth: Attempting sign in for:', credentials.username); }
        
        // Create Cognito service instance
        const cognitoService = new CognitoService();
        
        // Attempt sign in
        const result = await cognitoService.signIn(credentials.username, credentials.password);
        
        if (ISDEBUG) { console.log('Modern auth: Sign in successful for:', credentials.username); }
        return result;
        
    } catch (error) {
        if (ISDEBUG) { console.log('Modern auth: Error during sign in:', error); }
        
        // Handle MFA required case
        if (error instanceof MFARequiredError) {
            if (ISDEBUG) { console.log('Modern auth: MFA required for:', error.username); }
            
            // IMPORTANT: Set global variables for compatibility with existing MFA confirmation flow
            // This ensures the existing system can handle MFA confirmation
            mfaCredentials = {
                username: error.username,
                password: credentials.password
            };
            mfaSession = error.session;
            mfaUserPoolId = awsconfig.aws_user_pools_id;
            mfaClientId = awsconfig.aws_user_pools_web_client_id;
            
            if (ISDEBUG) { console.log('Modern auth: Set global MFA variables for compatibility'); }
            
            // Return challenge for UI
            return {
                challengeName: error.challengeName,
                challengeParam: { USER_ID_FOR_SRP: error.username },
                Session: error.session,
                username: error.username,
                isModernAuth: true // Flag to indicate this is the new auth flow
            };
        }
        
        // Handle other authentication errors
        if (error instanceof AuthenticationError) {
            if (ISDEBUG) { console.log('Modern auth: Authentication error:', error.code, error.message); }
            throw error;
        }
        
        // Generic error handling
        throw new Error(`Modern authentication failed: ${error.message}`);
    }
};

// NEW: Modern MFA confirmation using Cognito service
const modernMFAConfirmation = async (username, code, session, challengeName = 'EMAIL_OTP') => {
    try {
        if (ISDEBUG) { console.log('Modern MFA: Attempting confirmation for:', username); }
        
        // Create Cognito service instance
        const cognitoService = new CognitoService();
        
        // Confirm MFA
        const result = await cognitoService.confirmMFA(username, code, session, challengeName);
        
        if (ISDEBUG) { console.log('Modern MFA: Confirmation successful for:', username); }
        return result;
        
    } catch (error) {
        if (ISDEBUG) { console.log('Modern MFA: Error during confirmation:', error); }
        
        // Handle specific MFA errors
        if (error instanceof MFARequiredError) {
            // Additional MFA challenges might be required
            return {
                challengeName: error.challengeName,
                challengeParam: { USER_ID_FOR_SRP: error.username },
                Session: error.session,
                username: error.username,
                isModernAuth: true
            };
        }
        
        // Handle other MFA errors
        if (error instanceof AuthenticationError) {
            if (ISDEBUG) { console.log('Modern MFA: Authentication error:', error.code, error.message); }
            throw error;
        }
        
        // Generic error handling
        throw new Error(`Modern MFA confirmation failed: ${error.message}`);
    }
};

// UNIFIED: Authentication function that uses feature flag to choose system
const unifiedAuthSignIn = async (credentials) => {
    if (USE_MODERN_COGNITO) {
        if (ISDEBUG) { console.log('Unified auth: Using MODERN Cognito service'); }
        return await modernAuthSignIn(credentials);
    } else {
        if (ISDEBUG) { console.log('Unified auth: Using LEGACY AWS SDK v2'); }
        return await patchedAuthSignIn(credentials);
    }
};

// UNIFIED: MFA confirmation function that uses feature flag to choose system
const unifiedMFAConfirmation = async (username, code, session, challengeName = 'EMAIL_OTP') => {
    if (USE_MODERN_COGNITO) {
        if (ISDEBUG) { console.log('Unified MFA: Using MODERN Cognito service'); }
        return await modernMFAConfirmation(username, code, session, challengeName);
    } else {
        if (ISDEBUG) { console.log('Unified MFA: Using LEGACY AWS SDK v2'); }
        return await customMFAConfirmation(username, code);
    }
};

// Direct patch for the MFA bug using a more fundamental approach
// The issue is that we need to provide a working MFA flow that bypasses the broken AWS SDK code

// MFA Bug Fix Patch for AWS Amplify Auth 2.1.8
// This patch fixes the issue where Auth.signIn fails with MFA due to
// accessing undefined dataAuthenticate.AuthenticationResult.NewDeviceMetadata
// Issue occurs in CognitoUser.js:485 in the AWS SDK

// Global variables to store MFA state across function calls (used by legacy functions)
// These will be removed once we fully migrate to the modern Cognito service

const patchedAuthSignIn = async (credentials) => {
    // For MFA users, we need to skip Auth.signIn entirely to avoid duplicate codes
    // The NewDeviceMetadata bug occurs AFTER the MFA code is sent, so we can't catch it in time
    // Instead, we'll go straight to modern Cognito service for all users
    
    // Use modern CognitoService instead of legacy AWS SDK
    const { CognitoService } = await import('../services/CognitoService');
    const cognitoService = new CognitoService();
    
    try {
        // Use modern Cognito service for ALL users to avoid the duplicate MFA code issue
        const initiateAuthResult = await cognitoService.signIn(credentials.username, credentials.password);
        
        // Check if this is an MFA challenge
        if (initiateAuthResult.challengeName === 'EMAIL_OTP' || initiateAuthResult.challengeName === 'SMS_MFA' || initiateAuthResult.challengeName === 'SOFTWARE_TOKEN_MFA') {
            // Store credentials and session for MFA confirmation
            mfaCredentials = {
                username: credentials.username,
                password: credentials.password
            };
            mfaSession = initiateAuthResult.session;
            
            // Return challenge for UI
            return {
                challengeName: initiateAuthResult.challengeName,
                challengeParam: { USER_ID_FOR_SRP: credentials.username },
                Session: initiateAuthResult.session,
                username: initiateAuthResult.username
            };
        } else if (initiateAuthResult.signInUserSession) {
            // Non-MFA user - successful authentication
            // The modern service already returns the data in the correct format
            return initiateAuthResult;
        } else {
            throw new Error('Unexpected authentication result');
        }
        
    } catch (error) {
        throw error;
    }
};

// Custom MFA confirmation that bypasses the broken AWS SDK
const customMFAConfirmation = async (username, code) => {
    try {
        if (ISDEBUG) { console.log('Attempting custom MFA confirmation for:', username); }
        
        // Use modern CognitoService instead of legacy AWS SDK
        const { CognitoService } = await import('../services/CognitoService');
        const cognitoService = new CognitoService();
        
        // Use the stored session if available, otherwise try to get a new session
        if (mfaSession) {
            if (ISDEBUG) { console.log('Using stored MFA session...'); }
            
            if (ISDEBUG) { console.log('Responding to MFA challenge with stored session...'); }
            const mfaResult = await cognitoService.confirmMFA(username, code, mfaSession, 'EMAIL_OTP');
            
            if (ISDEBUG) { console.log('MFA confirmation successful:', mfaResult); }
            
            // The modern service already returns the data in the correct format
            return mfaResult;
        } else {
            // No stored session - cannot proceed without session data
            if (ISDEBUG) { console.log('No stored session available for MFA confirmation'); }
            throw new Error('MFA session expired. Please sign in again.');
        }
        
    } catch (error) {
        if (ISDEBUG) { console.log('Custom MFA confirmation failed:', error); }
        throw error;
    }
};

// Patched MFA confirmation that works around the session issue
const patchedConfirmSignIn = async (user, code, challengeName) => {
    try {
        if (ISDEBUG) { console.log('Attempting MFA confirmation with custom implementation'); }
        
        // Use our custom MFA confirmation that bypasses the AWS Amplify bug
        if (mfaCredentials) {
            const result = await customMFAConfirmation(mfaCredentials.username, code);
            
            // Clear stored credentials on success
            mfaCredentials = null;
            mfaSession = null;
            mfaUserPoolId = null;
            mfaClientId = null;
            
            return result;
        } else {
            throw new Error('No stored credentials for MFA confirmation');
        }
        
    } catch (error) {
        if (ISDEBUG) { console.log('MFA confirmation failed:', error); }
        throw error;
    }
};




//For use by signIn and also checkCredentials
const authenticationSuccess = sendToElmPayload => res => {
    
    let payloadMaybe = S.gets (S.is ($.Object)) (['signInUserSession', 'idToken', 'payload']) (res) //Maybe Object
    if(ISDEBUG) {console.log("payloadMaybe is", payloadMaybe)}

    let emailMaybe = S.chain (S.get (S.is ($.String)) ('email')) (payloadMaybe) //Maybe String
    let usernameMaybe = S.chain (S.get (S.is ($.String)) ('sub')) (payloadMaybe) //Maybe String
    let groups = S.pipe([
	S.chain (S.get (S.is ($.Unknown)) ('cognito:groups')),
	//S.fromMaybe (["Student"])
	S.fromMaybe ([])
    ]) (payloadMaybe) //Array String
    
    //Return a payload of either an error structure OR a userDetails structure (without password)
    const buildPayload = emailMaybe =>  usernameMaybe =>  groups => {
	let payloadMaybe = S.sequence (S.Maybe) ({email: emailMaybe, username: usernameMaybe}) //Maybe payloadStruct

	const invalidParamsToSignInPayload = () => ({
	    code: "InvalidParameters",
	    severity: ERROR_TYPE.severity.Fatal,
	    message: "There is a problem with the application configuration that means we cannot log you in.  Please report this message to SmarterMaths (support@smartermaths.com.au) and we'll get it fixed asap!",
	    details: `The amplify result from Auth.signIn has email as ${S.fromMaybe ("Nothing") (emailMaybe)} and the username as ${S.fromMaybe ("Nothing") (usernameMaybe)} but both are required!`
	})
	
	//If Just payload then add the groups, else return error structure
	let payload = S.pipe ([
	    S.map (S.concat ({groups: groups})),
	    S.fromMaybe (invalidParamsToSignInPayload())
	]) (payloadMaybe)

	//console.log("authenticationSuccess payload to send to elm is", payload);
	return payload;
    }

    const payload = buildPayload (emailMaybe) (usernameMaybe) (groups);

    if (ISDEBUG) {console.log('NapOnlBackendAccess.js: signIn: Input res', res)} 
    if (ISDEBUG) {console.log('NapOnlBackendAccess.js: signIn: payload', payload)}
    if (ISDEBUG) {console.log('NapOnlBackendAccess.js: signIn: sendToElmPayload', sendToElmPayload)}
    
    const resp = sendToElmPayload (payload);
    if (ISDEBUG) {console.log('NapOnlBackendAccess.js: signIn: Output resp', resp)}

    
}



// ***********************************
// signIn
// ***********************************
export const signIn = sendToElmPayload => msg => {
    //Returns  signInUserSession.idToken.payload. cognito:groups -> ["NapAdmin", "Teacher"], and sub, and email), else if fail, sendToElm error as specified below?
    // if not valid params, then sendToElm "UserSignedIn" BUT with the error details as below "There is a misconfiguration in the application and we cannot continue.  Please report to SmarterMaths"

    //const sendToElmPayload = sendToElm(INTEROP_TAGS.JTE.SignInJTE);
    //console.log("sendToElmPayload is", sendToElmPayload);
    
    const signInFailure = rej => {
	//Extract code, and message if they exist in the rej(ection) else create a generic failure message.
	if(ISDEBUG) {console.log ('rejection!', rej)}
	
	let codeMaybe = S.get (S.is ($.String)) ('code') (rej) //Maybe String
	let messageMaybe = S.get (S.is ($.String)) ('message') (rej) //Maybe String
	let error = {
	    code: S.fromMaybe ("SignInFailure") (codeMaybe),
	    severity: ERROR_TYPE.severity.Error,
	    message: S.fromMaybe ("Sorry but we were unable to sign you in.") (messageMaybe),
	    details: JSON.stringify(msg)
	}
	sendToElmPayload (error)
    }

    const signInMFAChallenge = (user, challengeName) => {
	if(ISDEBUG) {console.log ('MFA Challenge required!', user, challengeName)}
	
	// Send MFA challenge details to Elm
	let mfaChallenge = {
	    challengeName: challengeName,
	    session: user.Session || (user.challengeParam && user.challengeParam.SESSION) || null,
	    // Extract user identifier for the challenge
	    username: user.username || (user.challengeParam && user.challengeParam.USER_ID_FOR_SRP) || null,
	    // Additional challenge parameters if needed
	    challengeParameters: user.challengeParam || {}
	}
	
	if(ISDEBUG) {console.log ('Sending MFA challenge to Elm:', mfaChallenge)}
	sendToElmPayload (mfaChallenge)
    }
        
    const handleSignInResult = (result) => {
	if(ISDEBUG) {console.log ('SignIn result:', result)}
	if(ISDEBUG) {console.log ('SignIn result keys:', Object.keys(result))}
	if(ISDEBUG) {console.log ('SignIn result challengeName:', result.challengeName)}
	if(ISDEBUG) {console.log ('SignIn result signInUserSession:', result.signInUserSession)}
	
	// Check if this is an MFA challenge - AWS Cognito returns challengeName in the result when MFA is required
	if (result.challengeName && (result.challengeName.includes('MFA') || result.challengeName.includes('OTP'))) {
	    if(ISDEBUG) {console.log ('Detected MFA challenge')}
	    // Handle MFA challenge
	    signInMFAChallenge(result, result.challengeName)
	} else if (result.signInUserSession) {
	    if(ISDEBUG) {console.log ('Detected successful authentication')}
	    // Normal successful authentication - only call authenticationSuccess if we have a valid session
	    authenticationSuccess (sendToElmPayload) (result)
	} else {
	    // Unexpected result structure, treat as failure
	    if(ISDEBUG) {console.log ('Unexpected signIn result structure:', result)}
	    signInFailure({
		code: "UnexpectedSignInResult",
		message: "Unexpected authentication result structure"
	    })
	}
    }

    const trySignIn = (msg) =>
	  encaseP (creds => unifiedAuthSignIn(creds)) (msg.payload) //({username: 'smadmin1@smartermaths.com.au', password: 'Smnaplan1!'})
    	  .pipe(
    	      fork
	      (signInFailure)
	      (handleSignInResult))

    trySignIn(msg)
}


// ***********************************
// confirmMFACode
// ***********************************
export const confirmMFACode = sendToElmPayload => msg => {
    //Confirms the MFA code and completes the authentication process
    
    const confirmMFAFailure = rej => {
	//Extract code, and message if they exist in the rej(ection) else create a generic failure message.
	if(ISDEBUG) {console.log ('MFA confirmation rejection!', rej)}
	
	let codeMaybe = S.get (S.is ($.String)) ('code') (rej) //Maybe String
	let messageMaybe = S.get (S.is ($.String)) ('message') (rej) //Maybe String
	let error = {
	    code: S.fromMaybe ("MFAConfirmationFailure") (codeMaybe),
	    severity: ERROR_TYPE.severity.Error,
	    message: S.fromMaybe ("Sorry but the MFA code you entered is incorrect.") (messageMaybe),
	    details: JSON.stringify(msg)
	}
	sendToElmPayload (error)
    }

    const tryConfirmMFA = (msg_) =>
	  encaseP (params => patchedConfirmSignIn(params.user, params.code, params.challengeName)) (msg_.payload)
    	  .pipe(
    	      fork
	      (confirmMFAFailure)
    	      (authenticationSuccess (sendToElmPayload)))

    const msgIsValidFormat = (msg_) => {
	let hasUser = S.isJust (S.gets (S.is ($.Unknown)) (['payload', 'user']) (msg_));
	let hasCode = S.isJust (S.gets (S.is ($.String)) (['payload', 'code']) (msg_));
	let hasChallengeName = S.isJust (S.gets (S.is ($.String)) (['payload', 'challengeName']) (msg_));
	
	return (S.all (S.equals (true)) ([hasUser, hasCode, hasChallengeName]));
    }

    const invalidMsgFields = (msg) => sendToElmPayload ({
	code: "InvalidMessageFieldsPassedToJs",
	severity: ERROR_TYPE.severity.Fatal,
	message: "There is a problem with the application configuration that means we cannot complete MFA authentication. Please report this message to SmarterMaths (support@smartermaths.com.au) and we'll get it fixed asap!",
	details: `An invalid request was made from elm to js for the confirmMFACode request which should contain user, code, and challengeName but was called with the following message format [${JSON.stringify(msg)}]`
    });
    
    //The input msg params (should contain a tag and a data field) will be validated here once, so when call functions, can know msg.payload exists
    S.ifElse (msgIsValidFormat) (tryConfirmMFA) (invalidMsgFields) (msg);
}



// ***********************************
// forgotPassword
// ***********************************
export const forgotPassword = sendToElmPayload => msg => {
    //Returns  signInUserSession.idToken.payload. cognito:groups -> ["NapAdmin", "Teacher"], and sub, and email), else if fail, sendToElm error as specified below?
    // if not valid params, then sendToElm "UserSignedIn" BUT with the error details as below "There is a misconfiguration in the application and we cannot continue.  Please report to SmarterMaths"

    //const sendToElmPayload = sendToElm(INTEROP_TAGS.JTE.ForgotPasswordJTE);

    const forgotPasswordFailure = rej => {
	//Extract code, and message if they exist in the rej(ection) else create a generic failure message.
	if(ISDEBUG) {console.log ('rejection!', rej)}
	
	let codeMaybe = S.get (S.is ($.String)) ('code') (rej) //Maybe String
	let messageMaybe = S.get (S.is ($.String)) ('message') (rej) //Maybe String
	let error = {
	    code: S.fromMaybe ("ForgotPasswordFailure") (codeMaybe),
	    severity: ERROR_TYPE.severity.Error,
	    message: S.fromMaybe ("Sorry but we were unable to reqest the forgot password code in.") (messageMaybe),
	    details: JSON.stringify(msg)
	}
	sendToElmPayload (error)
    }
    
    const forgotPasswordSuccess = username => res => {

	let payloadMaybe = S.get (S.is ($.Object)) ('CodeDeliveryDetails') (res) //Maybe Object
        if(ISDEBUG) {console.log("payloadMaybe is", payloadMaybe)}

	let deliveryMediumMaybe = S.chain (S.get (S.is ($.String)) ('DeliveryMedium')) (payloadMaybe) //Maybe String
	let destinationMaybe = S.chain (S.get (S.is ($.String)) ('Destination')) (payloadMaybe) //Maybe String
	// let groups = S.pipe([
	//     S.chain (S.get (S.is ($.Unknown)) ('cognito:groups')),
	//     //S.fromMaybe (["Student"])
	//     S.fromMaybe ([])
	// ]) (payloadMaybe) //Array String
		
	//Return a payload of either an error structure OR a userDetails structure (without password)
	const buildPayload = deliveryMediumMaybe => destinationMaybe => {
	    let payloadMaybe = S.sequence (S.Maybe) ({username: S.Just(username), deliveryMedium: deliveryMediumMaybe, destination: destinationMaybe}) //Maybe payloadStruct

	    const invalidParamsToSignInPayload = () => ({
		code: "InvalidParameters",
		severity: ERROR_TYPE.severity.Fatal,
		message: "There is a problem with the application configuration that means we cannot log you in.  Please report this message to SmarterMaths (support@smartermaths.com.au) and we'll get it fixed asap!",
		details: `The amplify result from Auth.forgotPassword has deliveryMedium as ${S.fromMaybe ("Nothing") (deliveryMediumMaybe)} and the destination as ${S.fromMaybe ("Nothing") (destinationMaybe)} but both should be present.`
	    })
	    
	    //If Just payload then add the groups, else return error structure
	    let payload = S.fromMaybe (invalidParamsToSignInPayload()) (payloadMaybe)
		
	    return payload;
	}
		
	sendToElmPayload (buildPayload (deliveryMediumMaybe) (destinationMaybe));
	if (ISDEBUG) {console.log('NapOnlBackendAccess.js: forgotPassword', res)}
    }
    
    const tryForgotPassword = (msg_) =>
	  encaseP (uname => Auth.forgotPassword(uname)) (msg_.payload.username) //({username: 'smadmin1@smartermaths.com.au'})
    	  .pipe(
    	      fork
	      (forgotPasswordFailure)
    	      (forgotPasswordSuccess(msg_.payload.username)))

    const msgIsValidFormat = (msg_) => {
	let hasUsername = S.isJust (S.gets (S.is ($.String)) (['payload', 'username']) (msg_));
	return hasUsername;
    }

    const invalidMsgFields = (msg) => sendToElm (INTEROP_TAGS.JTE.InteropFailureJTE) ({
	code: "InvalidMessageFieldsPassedToJs",
	severity: ERROR_TYPE.severity.Fatal,
	message: "There is a problem with the application configuration that means we cannot log you in.  Please report this message to SmarterMaths (support@smartermaths.com.au) and we'll get it fixed asap!",
	details: `An invalid request was made from elm to js for the forgetPassword request which should contain a username but was called with the with the following message format [${JSON.stringify(msg)}]`
    });
    
    //The input msg params (should contain a tag and a data field) will be validated here once, so when call functions, can know msg.payload exists
    S.ifElse (msgIsValidFormat) (tryForgotPassword) (invalidMsgFields) (msg);
    //tryForgotPassword("matt@turtleroad.com")
}



// ***********************************
// forgotPasswordSubmit
// ***********************************
export const forgotPasswordSubmit = sendToElmPayload => msg => {
    //Returns  signInUserSession.idToken.payload. cognito:groups -> ["NapAdmin", "Teacher"], and sub, and email), else if fail, sendToElm error as specified below?
    // if not valid params, then sendToElm "UserSignedIn" BUT with the error details as below "There is a misconfiguration in the application and we cannot continue.  Please report to SmarterMaths"

    //const sendToElmPayload = sendToElm(INTEROP_TAGS.JTE.ForgotPasswordSubmitJTE);

    const forgotPasswordSubmitFailure = rej => {
	//Extract code, and message if they exist in the rej(ection) else create a generic failure message.
	if(ISDEBUG) {console.log ('rejection!', rej)}
	
	let codeMaybe = S.get (S.is ($.String)) ('code') (rej) //Maybe String
	let messageMaybe = S.get (S.is ($.String)) ('message') (rej) //Maybe String
	let error = {
	    code: S.fromMaybe ("ForgotPasswordSubmitFailure") (codeMaybe),
	    severity: ERROR_TYPE.severity.Error,
	    message: S.fromMaybe ("Sorry but we were unable to reqest the forgot password code in.") (messageMaybe),
	    details: JSON.stringify(msg)
	}
	sendToElmPayload (error)
    }
    
    const forgotPasswordSubmitSuccess = res => {
	sendToElmPayload ("Success");
	if (ISDEBUG) {console.log('NapOnlBackendAccess.js: forgotPasswordSubmit: [Note - Successful promise resolution res is void] so the string "Success" is sent back to elm', res)}		
    }
    
    const tryForgotPasswordSubmit = (msg_) =>
	  encaseP (p => Auth.forgotPasswordSubmit(p.username, p.code, p.password)) (msg_.payload) //({username: 'smadmin1@smartermaths.com.au'})
    	  .pipe(
    	      fork
	      (forgotPasswordSubmitFailure)
    	      (forgotPasswordSubmitSuccess))

    const msgIsValidFormat = (msg_) => {
	let hasUsername = S.isJust (S.gets (S.is ($.String)) (['payload', 'username']) (msg_));
	let hasCode = S.isJust (S.gets (S.is ($.String)) (['payload', 'code']) (msg_));
	let hasNewPassword = S.isJust (S.gets (S.is ($.String)) (['payload', 'password']) (msg_));

	return (S.all (S.equals (true)) ([hasUsername, hasCode, hasNewPassword]));
    }

    
    const invalidMsgFields = (msg) => sendToElm (INTEROP_TAGS.JTE.InteropFailureJTE) ({
	code: "InvalidMessageFieldsPassedToJs",
	severity: ERROR_TYPE.severity.Fatal,
	message: "There is a problem with the application configuration that means we cannot log you in.  Please report this message to SmarterMaths (support@smartermaths.com.au) and we'll get it fixed asap!",
	details: `An invalid request was made from elm to js for the forgetPasswordSubmit request which should contain a username but was called with the with the following message format [${JSON.stringify(msg)}]`
    });
    
    //The input msg params (should contain a tag and a data field) will be validated here once, so when call functions, can know msg.payload exists
    S.ifElse (msgIsValidFormat) (tryForgotPasswordSubmit) (invalidMsgFields) (msg);
    //tryForgotPassword("matt@turtleroad.com")
}




// ***********************************
// signOut
// ***********************************
export const signOut = sendToElmPayload => msg => {
    const signOutFailure = rej => {
	//Extract code, and message if they exist in the rej(ection) else create a generic failure message.
	if(ISDEBUG) {console.log ('rejection!', rej)}
	
	let codeMaybe = S.get (S.is ($.String)) ('code') (rej) //Maybe String
	let messageMaybe = S.get (S.is ($.String)) ('message') (rej) //Maybe String
	let error = {
	    code: S.fromMaybe ("SignOutFailure") (codeMaybe),
	    severity: ERROR_TYPE.severity.Error,
	    message: S.fromMaybe ("Sorry but we were unable to successfully sign you out.") (messageMaybe),
	    details: JSON.stringify(msg)
	}
	sendToElmPayload (error)
    }
    
    const signOutSuccess = res => {
	sendToElmPayload ("Success");
	if (ISDEBUG) {console.log('NapOnlBackendAccess.js: signOut: [Note - Successful promise resolution res is void] so the string "Success" is sent back to elm', res)}		
    }
    
    const trySignOut = () =>
	  encaseP (el => Auth.signOut()) () 
    	  .pipe(
    	      fork
	      (signOutFailure)
    	      (signOutSuccess))

    trySignOut()
}

// When browser is refreshed this is called to re-send login details if the user is still logged in.
export const handleRefreshIfAlreadyAuthenticated = (sendToElm) => {
    if (ISDEBUG) {console.log('SignInOut.js: handleRefreshIfAlreadyAuthenticated')}
    const sendToElmPayload = sendToElm (INTEROP_TAGS.SignInJTE);

    const tryRefresh = () =>
	  encaseP (el => Auth.currentAuthenticatedUser()) () //No params 
    	  .pipe(
    	      fork
	      (el => console.log("Not authenticated, so will show signIn page"))
    	      (authenticationSuccess (sendToElmPayload)))

    tryRefresh()
}

